import { bounds, clipBand, hand, pointInPoly, polyArea, pt, type Pt } from './geom';

/**
 * **填充** —— 把一块形状变成墨。
 *
 * ⚠️ 这里**不做贴图**。半调点阵和 45° 条纹都是**算出来的几何**：
 *
 * - 网点：栅格点逐个做「点在多边形内」判定 —— 点永远不会漏到形状外面
 * - 条纹：Sutherland–Hodgman 把形状裁成一串斜条 —— 边缘是精确的直线
 *
 * 用贴图的版本会在缩放、DPR、旋转上各出一遍问题（接缝、摩尔纹、
 * 低分辨率下糊成一团），而算出来的几何和分辨率**完全无关**，
 * 还能在 node 里直接断言。代价只是构造时多算几千个点，只算一次。
 */

/** 一个网点 */
export interface Dot {
  readonly x: number;
  readonly y: number;
  readonly r: number;
}

/** 覆盖率 → 网点半径。`0.5` 就是"50% 灰"：πr²/pitch² = 0.5 */
export function dotRadius(pitch: number, coverage: number): number {
  const k = coverage < 0 ? 0 : coverage > 1 ? 1 : coverage;
  return pitch * Math.sqrt(k / Math.PI);
}

/**
 * 半调网点。
 *
 * @param coverage 常数，或一个按位置取覆盖率的函数（做渐变/半印用）
 */
export function halftoneDots(
  poly: readonly Pt[],
  pitch: number,
  angleDeg = 45,
  coverage: number | ((p: Pt) => number) = 0.5,
): Dot[] {
  if (poly.length < 3 || pitch <= 0) return [];
  const b = bounds(poly);
  const a = (angleDeg * Math.PI) / 180;
  const ca = Math.cos(a);
  const sa = Math.sin(a);
  const cx = (b.minX + b.maxX) / 2;
  const cy = (b.minY + b.maxY) / 2;
  // 覆盖半径：旋转之后的栅格要能盖住外接圆
  const reach = Math.hypot(b.maxX - b.minX, b.maxY - b.minY) / 2 + pitch;
  const n = Math.ceil(reach / pitch);
  const fn = typeof coverage === 'function' ? coverage : (): number => coverage;
  const out: Dot[] = [];
  for (let j = -n; j <= n; j++) {
    // 隔行错开半格 —— 正方栅格排出来的网点一眼就看出是格子，不是印刷
    const stagger = (j & 1) === 0 ? 0 : 0.5;
    for (let i = -n; i <= n; i++) {
      const u = (i + stagger) * pitch;
      const v = j * pitch;
      const x = cx + u * ca - v * sa;
      const y = cy + u * sa + v * ca;
      if (!pointInPoly(pt(x, y), poly)) continue;
      const r = dotRadius(pitch, fn(pt(x, y)));
      if (r > 0.02) out.push({ x, y, r });
    }
  }
  return out;
}

/**
 * 斜条纹：把形状裁成一串平行的斜条。
 *
 * ⚠️ **异常牌底部的危险条纹、和濒死状态的横带用的是同一个函数。**
 * 这不是省事 —— 是让"禁止/致命"在整套视觉里只有一个写法。
 *
 * @param duty 条纹占空比（0.5 = 等宽相间）
 */
export function hazardBands(
  poly: readonly Pt[],
  pitch: number,
  angleDeg: number,
  duty = 0.5,
  phase = 0,
): Pt[][] {
  if (poly.length < 3 || pitch <= 0) return [];
  const a = (angleDeg * Math.PI) / 180;
  const nx = Math.cos(a);
  const ny = Math.sin(a);
  let lo = Infinity;
  let hi = -Infinity;
  for (const p of poly) {
    const t = nx * p.x + ny * p.y;
    if (t < lo) lo = t;
    if (t > hi) hi = t;
  }
  // ⚠️ 相位要靠**移动条带序号**实现，不能靠平移形状 ——
  //    平移形状会同时移动覆盖范围，边上会缺一条
  //    （`phase` 是本项目里唯一需要"让条纹动起来"的地方：濒死横带）
  const k0 = Math.floor(lo / pitch) - 1;
  const k1 = Math.ceil(hi / pitch) + 1;
  const out: Pt[][] = [];
  for (let k = k0; k <= k1; k++) {
    const s = (k + phase) * pitch;
    const piece = clipBand(poly, nx, ny, s, s + pitch * duty);
    // ⚠️ 面积判定不能省：裁到边角上会退化成一条线或一个点，
    // 直接画出来就是一根飘在空中的蜘蛛丝
    if (piece.length >= 3 && polyArea(piece) > 0.02) out.push(piece);
  }
  return out;
}

/**
 * 来回涂死。
 *
 * ⚠️ 素人画东西就是这样涂满的，不是平涂。只涂一个方向会读成"夹层"，
 * 所以真正要用的时候**横竖各来一道** —— 见 `crossScribble()`。
 */
export function scribbleFill(
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  rows: number,
  seed: number,
): Pt[] {
  const p: Pt[] = [];
  const r = Math.max(1, Math.round(rows));
  for (let k = 0; k <= r; k++) {
    const v = -1 + (2 * k) / r;
    const half = rx * Math.sqrt(Math.max(0, 1 - v * v));
    const y = cy + ry * v;
    if (k % 2 === 0) {
      p.push(pt(cx - half, y), pt(cx + half, y));
    } else {
      p.push(pt(cx + half, y), pt(cx - half, y));
    }
  }
  return hand(p, seed, 1.4, 2.4);
}

/**
 * 横竖各涂一道 —— 真正"涂死"的样子。
 * 只涂一道会读成夹层汉堡（踩过）。
 */
export function crossScribble(
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  rows: number,
  seed: number,
): { a: Pt[]; b: Pt[] } {
  // 第二道在**转置的空间**里生成再换回来 —— 等价于"竖着涂"。
  // 转置后中心是 (cy,cx)、半径是 (ry,rx)，换回来正好落回原来的椭圆
  const vert = scribbleFill(cy, cx, ry, rx, rows, seed ^ 0x12).map((p) => pt(p.y, p.x));
  return { a: scribbleFill(cx, cy, rx, ry, rows, seed), b: vert };
}
